import { setNewRelicUserId } from '@/external_scripts/newrelic.ts'
import CustomerMetaSchema from '@/modules/schemas/customer/CustomerMetaSchema'
import CookieHelper from '@/services/helpers/CookieHelper'
import { defineStore } from 'pinia'
import { nextTick } from 'vue'

const ACCESS_TOKEN_EXPIRATION = 1000 * 60 * 59
const SOFT_LOGIN_COOKIE = 'soft_login'
const NR_SOURCE_AUTH_LOGIN_UNKNOWN = 'auth_login_unknown'
const NR_SOURCE_AUTH_2FA_UNKNOWN = 'auth_2fa_unknown'
const NR_SOURCE_AUTH_CONFIRM_2FA_UNKNOWN = 'auth_confirm_2fa_unknown'
const LAST_REFRESH = 'last_refresh'
const API_KEY_AGE = 'apiKey_age'
const API_KEY = 'apiKey'
export const useCustomerStore = defineStore('customer', {
  state() {
    return {
      pending: false,
      profile: CustomerMetaSchema.fields,
      profileV2: null,
      addresses: null,
      paymentMethods: null,
      payment: {
        token: null
      },
      active: false,
      softLoggedIn: false,
      isLoginShowing: false,
      lastSoftLoginCookie: null,
      adminPermissionError: false,
      presets: {
        email: null,
        password: null,
        totpSetupRequired: false
      },
      hooks: {
        afterLogin: null,
        afterLogout: null,
        afterRegister: null
      }
    }
  },
  getters: {
    customerProfile: ({ profile, profileV2 }) => {
      const originalCustomerId = CookieHelper.getCookie('original-customer-id')
      return originalCustomerId ? profileV2 : profile
    },
    customerSoftLoggedIn: ({ active, softLoggedIn }) => active || softLoggedIn,
    customerId() {
      return (
        this.customerProfile?.customer_id ?? this.customerProfile?.id ?? null
      )
    },
    customerActive: ({ active }) => active,
    customerIsAdmin: ({ profile: { is_admin: isAdmin }, profileV2 }) => {
      return isAdmin || !!profileV2?.admin_data
    },
    customerIsPending: ({ pending }) => pending,
    customerPaymentToken: ({ payment: { token } }) => token,
    adminLoggedInAs: ({ profile }) => {
      // First check profile data (for backward compatibility)
      if (profile?.admin_data?.admin_customer_id) {
        return profile.admin_data.admin_customer_id
      }

      // Then check cookie (for new admin impersonation feature)
      if (typeof document !== 'undefined') {
        try {
          const cookieValue = CookieHelper.getCookie('original-customer-id')
          return cookieValue || null
        } catch (error) {
          console.error(error, 'Original customer id cookie not found')
          return null
        }
      }
      return null
    },
    isNewsletterSubscribed: ({
      profile: { is_newsletter_subscribed: isNewsletterSubscribed }
    }) => isNewsletterSubscribed,
    customerPresets: ({ presets }) => presets,
    hasDob: ({ profile: { dob } }) => !!dob,
    hasMilesAndMore: ({ profile: { miles_and_more: milesAndMore } }) =>
      milesAndMore,
    showAdminPermissionError: ({ adminPermissionError }) =>
      adminPermissionError,
    salutation: ({ profileV2 }) => profileV2?.salutation
  },
  actions: {
    async establishSession() {
      // already in progress, bail out
      if (this.pending) return

      this.pending = true
      // check if there has been an active session previously
      await this.checkPreviousSession()
      // check for soft login
      await this.checkSoftLogin()
      void this.fetchPaymentToken()

      // no soft login nor active session, bail out
      if (!this.active && !this.softLoggedIn) {
        this.pending = false
        return
      }

      // get profile
      await this.fetchProfile()

      if (!this.profile.customer_id) {
        /*
          Login was not successful, bail out
          This can happen if the app is recreated and the CookieHelper.isSet tests a httpOnly cookie, but it doesn't expire before the next load (1sec)
         */
        this.pending = false
        this.active = false
        this.softLoggedIn = false
        return
      }

      try {
        // get a fresh access token, if soft login: nothing to do here
        await _handleRefreshToken({
          profile: this.profile,
          softLoggedIn: this.softLoggedIn
        })
      } catch (e) {
        await _handleError(e)
      }

      // all done, session established
      this.pending = false
    },
    async fetchProfile(force = false) {
      if (!(await this._shouldRefetchProfile(force))) return

      try {
        await this._fetchProfileData()
        await this._updateSoftLoginReference()
        await this._initializeProfile()
      } catch (e) {
        this.softLoggedIn = false
        console.error(e)
      }
    },

    async _shouldRefetchProfile(force) {
      if (force) return true
      // If no customer_id, always refetch
      if (!this.profile.customer_id) return true
      // If not soft logged in and not active, don't refetch
      if (!this.softLoggedIn && !this.active) return false
      // If active (fully logged in), don't refetch unless forced
      if (this.active && !force) return false

      // For soft login users, check if cookie changed
      const { CookieHelper } = await _getModules()
      const currentSoftLoginCookie = CookieHelper.getCookie(SOFT_LOGIN_COOKIE)

      if (
        currentSoftLoginCookie &&
        currentSoftLoginCookie !== this.lastSoftLoginCookie
      ) {
        this.lastSoftLoginCookie = currentSoftLoginCookie
        return true
      }

      return false
    },

    async _fetchProfileData() {
      const { ApiController } = await _getModules()

      const [profile, profileV2] = await Promise.all([
        ApiController.fetchCustomerProfile(),
        ApiController.fetchCustomerProfileV2()
      ])

      let customerProfile, customerProfileV2

      // Check for inconsistency between APIs (common during soft login switches)
      if (this.softLoggedIn && profile?.customer_id !== profileV2?.id) {
        // Use Profile V2 as source of truth and map to Profile API format
        customerProfile = this._mapProfileV2ToProfileFormat(profileV2, profile)
        customerProfileV2 = profileV2
      } else {
        // APIs are consistent or regular user, use data as-is
        customerProfile = profile
        customerProfileV2 = profileV2
      }

      // doi_show_checkbox only exists on the v2 profile response (VIP-12044); overlay
      // it onto profile since that's the object components already read from.
      this.profile = {
        ...customerProfile,
        doi_show_checkbox: customerProfileV2?.doi_show_checkbox ?? null
      }
      this.profileV2 = customerProfileV2
      setNewRelicUserId(this.customerId)
    },

    _mapProfileV2ToProfileFormat(profileV2, fallbackProfile) {
      return {
        ...profileV2,
        salutation: profileV2.salutation ?? fallbackProfile?.salutation,
        // Preserve other Profile API fields
        is_admin: fallbackProfile?.is_admin,
        meta_navigation: fallbackProfile?.meta_navigation,
        credits_formatted: fallbackProfile?.credits_formatted,
        credits_expire_days_left: fallbackProfile?.credits_expire_days_left,
        flash_messages: fallbackProfile?.flash_messages ?? [],
        admin_data: fallbackProfile?.admin_data ?? {},
        admin_url_backend: fallbackProfile?.admin_url_backend ?? {},
        state: fallbackProfile?.state ?? {},
        completed_orders_count: fallbackProfile?.completed_orders_count ?? {},
        days_since_last_order: fallbackProfile?.days_since_last_order ?? {},
        is_newsletter_subscribed: fallbackProfile?.is_newsletter_subscribed,
        dob: fallbackProfile?.dob,
        ip_address: fallbackProfile?.ip_address,
        phone: fallbackProfile?.phone,
        miles_and_more_card_nr: fallbackProfile?.miles_and_more_card_nr
      }
    },

    async _updateSoftLoginReference() {
      if (this.softLoggedIn) {
        const { CookieHelper } = await _getModules()
        this.lastSoftLoginCookie = CookieHelper.getCookie(SOFT_LOGIN_COOKIE)
      }
    },

    async _initializeProfile() {
      await nextTick()
      await _initTagManager(this.profile)
    },
    async fetchPaymentToken() {
      // token not needed or is already setup, bail out
      if (this.payment.token) return
      // get token otherwise
      const { ApiController } = await _getModules()
      const { braintree_client_token: bct } =
        await ApiController.fetchCustomerBraintreeToken()
      this.payment.token = bct
    },
    async waitUntilPending() {
      while (this.customerIsPending) await _wait()
      return Promise.resolve()
    },
    async waitForToken() {
      while (!this.customerPaymentToken) await _wait()
      return Promise.resolve()
    },
    async checkPreviousSession() {
      const { CookieHelper } = await _getModules()
      const active = !!CookieHelper.getCookie(API_KEY_AGE)
      if (active) {
        CookieHelper.invalidate(SOFT_LOGIN_COOKIE)
      }
      this.active = active
    },
    async checkSoftLogin() {
      if (this.active) return
      this.softLoggedIn = await _handleSoftLogin()
    },
    async logout() {
      const { ApiController, CookieHelper } = await _getModules()
      await ApiController.logoutCustomer()
      CookieHelper.invalidate('cartUuid')

      this.active = false
      this.softLoggedIn = false
      this.profile = CustomerMetaSchema.fields
      setNewRelicUserId(null)
    },
    async login({ email, password }) {
      const { ApiController, MessageHelper, CookieHelper } = await _getModules()
      try {
        await ApiController.loginCustomer({ email, password })
        CookieHelper.invalidate(SOFT_LOGIN_COOKIE)
        await this.establishSession()
      } catch (e) {
        const { status, cause } = e

        // Expected user errors (wrong credentials, 2FA required) are shown but
        // not reported to the backend log (VIP-12034)
        if (cause === 1011 || cause === 1012) {
          await MessageHelper.addErrorMessage('login_error_invalid_email')
        } else if (cause === 1014) {
          this.presets = {
            email,
            password,
            totpSetupRequired: e.errorData?.totp_setup_required ?? true
          }
          await MessageHelper.addErrorMessage(
            'two_factor_authentication_required'
          )
        } else if (status === 401 || cause === 1013) {
          await MessageHelper.addErrorMessage('please_check_your_data')
        } else {
          await MessageHelper.addErrorMessage(
            'unknown_error',
            null,
            5000,
            e,
            NR_SOURCE_AUTH_LOGIN_UNKNOWN
          )
        }
        throw e
      }
      _runHook(this.hooks.afterLogin)
    },
    async login2FA({ code }) {
      const { email, password } = this.presets
      const { ApiController, MessageHelper } = await _getModules()
      const MESSAGES = {
        401: 'please_check_your_data',
        500: 'server_error'
      }
      try {
        await ApiController.verifyTwoFactorAuth({ email, password, code })
        await ApiController.loginCustomer({ email, password })
        await this.establishSession()
        _runHook(this.hooks.afterLogin)
      } catch (error) {
        const { status, cause } = error
        if (cause === 1021) {
          await MessageHelper.addErrorMessage('invalid_2fa_code')
          await Promise.reject(new Error('invalid_2fa_code'))
        } else {
          await MessageHelper.addErrorMessage(
            MESSAGES[status] || 'unknown_error',
            null,
            5000,
            error,
            NR_SOURCE_AUTH_2FA_UNKNOWN
          )
        }
      }
    },
    async setupTwoFactor() {
      const { email, password } = this.presets
      const { ApiController } = await _getModules()
      return ApiController.getTwoFactorSetup({ email, password })
    },
    async confirmTwoFactorSetup({ code }) {
      const { email, password } = this.presets
      const { ApiController, MessageHelper } = await _getModules()
      try {
        await ApiController.confirmTwoFactorSetup({ email, password, code })
        await ApiController.loginCustomer({ email, password })
        await this.establishSession()
        _runHook(this.hooks.afterLogin)
      } catch (error) {
        const { cause } = error
        if (cause === 1021) {
          await MessageHelper.addErrorMessage('invalid_2fa_code')
        } else if (cause === 1022) {
          await MessageHelper.addErrorMessage('totp_setup_not_initiated')
        } else {
          await MessageHelper.addErrorMessage(
            'unknown_error',
            null,
            5000,
            error,
            NR_SOURCE_AUTH_CONFIRM_2FA_UNKNOWN
          )
        }
        throw error
      }
    },
    async register({
      email,
      firstName,
      lastName,
      gender,
      title,
      password,
      dob
    }) {
      const { ApiController } = await _getModules()

      await ApiController.registerCustomer({
        email,
        firstName,
        lastName,
        gender,
        title,
        password,
        dob
      })

      await ApiController.loginCustomerWithApiKey()
      await this.establishSession()
    },
    async showLogin({ visible, email, hook, hookParams }) {
      this.presets.email = email ?? null
      this.hooks.afterLogin = () => {
        this.isLoginShowing = false
        if (hook && hookParams) hook(...hookParams)
        if (hook && !hookParams) hook()
      }
      this.isLoginShowing = visible ?? false
    },
    async updateCustomerData(data) {
      const { ApiController } = await _getModules()
      await ApiController.updateCustomerData(data)
      await this.fetchProfile()
    },
    setAdminPermissionError(show) {
      this.adminPermissionError = show
    }
  }
})

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: SiteConfig },
      { default: TagManager },
      { default: ApiController },
      { default: CookieHelper },
      { default: MessageHelper }
    ] = await Promise.all([
      await import('@/services/SiteConfig'),
      await import('@/services/analytics/TagManager'),
      await import('@/services/ApiController'),
      await import('@/services/helpers/CookieHelper'),
      await import('@/services/helpers/MessageHelper')
    ])
    cache = {
      SiteConfig,
      TagManager,
      ApiController,
      CookieHelper,
      MessageHelper
    }
    return cache
  }
})()

const _initTagManager = async (profile) => {
  const { TagManager } = await _getModules()
  void TagManager.initCustomerData(profile)
}

const _handleRefreshToken = async ({
  profile: { admin_data: admin, customer_id: id },
  softLoggedIn
}) => {
  // no customer or soft login
  if (softLoggedIn) return
  // admin is logged in as customer
  if (admin?.admin_customer_id && admin.admin_customer_id !== id) return

  const { ApiController, CookieHelper } = await _getModules()
  const lastRefresh = parseInt(CookieHelper.getCookie(LAST_REFRESH) || 0)
  const now = Date.now()

  const difference = now - lastRefresh

  if (difference < ACCESS_TOKEN_EXPIRATION) return

  await ApiController.refreshCustomerToken()
}

const _handleSoftLogin = async () => {
  const { CookieHelper } = await _getModules()

  const { groups: grpEmail } =
    /email=(?<email>[^&]*)/.exec(location.search) ?? {}
  const { groups: grpHash } = /ph=(?<ph>[^&]*)/.exec(location.search) ?? {}

  if (grpEmail && grpHash) {
    const cookie = JSON.stringify({ ...grpEmail, ...grpHash })
    CookieHelper.setCookie(SOFT_LOGIN_COOKIE, cookie, 1)

    return true
  }
  return !!CookieHelper.getCookie(SOFT_LOGIN_COOKIE).length
}

const _handleError = async (e) => {
  console.error(e)
  const { SiteConfig, CookieHelper } = await _getModules()
  CookieHelper.invalidate('cartUuid')

  await nextTick()
  location.replace(
    `https://${SiteConfig.domain}/logout?redirect_uri=${location.pathname}`
  )
}

const _runHook = (hook) => {
  if (typeof hook !== 'function') {
    return
  }
  hook()
}

const _wait = async () =>
  new Promise((resolve) => {
    setTimeout(resolve, 100)
  })
